<template>
  <div
    class="flex relative"
    ref="actionPicker"
    :data-test="`actionPicker${dataTest}`"
  >
    <!-- Trigger Element -->
    <div
      ref="trigger"
      class="trigger"
      :class="{ disabled, 'full-width': contentFullWidth }"
      aria-describedby="actionPicker"
      @click.stop="handleClick"
    >
      <slot name="trigger"></slot>
    </div>

    <!-- Action Picker Content -->
    <div
      ref="actionPickerContent"
      class="action-picker"
      v-if="actionPickerActive"
      role="actionPicker"
      @click.stop
      data-test="action-picker-content"
    >
      <div
        class="action-picker-scroller"
        :style="{ maxHeight: maxHeight }"
      >
        <div
          class="dropdown-item"
          v-for="(action, index) in actionsParsed"
          :key="index"
          :data-cy="`${dataCy}Action-${index}`"
          @click="!action.disabled ? handleSelected(action) : null"
          :class="[[action.disabled ? 'disabled' : null], [action.type && !disabled ? action.type : null]]"
          :data-test="`action-${index}`"
        >
          <!-- Icon -->
          <div
            v-if="hasIcons"
            v-html="action.icon"
            class="mr-2 flex h-5 w-5 items-center justify-center text-sm"
          ></div>

          <!-- Action Text -->
          <div>
            {{ action.text }}
            <div
              class="disabled-message"
              v-if="action.disabled_text"
            >
              {{ action.disabled_text }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { createPopper } from '@popperjs/core';
import { setActiveActionPicker, getActiveActionPicker, closeActiveActionPicker } from '@/helpers/actionPickerManager'; // This should now be renamed

// Define props using defineProps
const props = defineProps({
  actions: {
    type: String,
    default: '[]',
  },
  dataTest: String,
  disabled: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,
    default: '320px',
  },
  trigger: {
    type: String,
    default: 'click',
    validator: (value) => ['hover', 'click'].includes(value),
  },
  contentFullWidth: {
    type: Boolean,
    default: false,
  },
});

// Setup local state
const actionPickerActive = ref(false);
const triggerEl = ref(null);
const actionPickerContentEl = ref(null);

// Create `emit` function using defineEmits
const emit = defineEmits();

// Actions parsed from JSON
const actionsParsed = computed(() => JSON.parse(props.actions));

// Computed property to check for icons
const hasIcons = computed(() => {
  return actionsParsed.value.some((action) => action.icon);
});

// Handle selection of an action and emit appropriate event
const handleSelected = (action) => {
  actionPickerActive.value = false;
  closeActiveActionPicker(); // Ensure the active action picker is closed
  if (action.emit) {
    emit(action.emit, action); // Dynamically emit event based on action.emit property
  }
};

// Event handlers for showing/hiding action picker
const handleClick = () => {
  if (!props.disabled) {
    if (!actionPickerActive.value) {
      closeActiveActionPicker();
      actionPickerActive.value = true;
      setActiveActionPicker(() => (actionPickerActive.value = false));
    } else {
      actionPickerActive.value = false;
      closeActiveActionPicker();
    }
  }
};

// Initialize Popper
const initPopper = () => {
  createPopper(triggerEl.value, actionPickerContentEl.value, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });
};

// Setup lifecycle hooks
onMounted(() => {
  initPopper();
  document.addEventListener('click', onOutsideClick); // Listen for clicks outside
});
onUnmounted(() => {
  document.removeEventListener('click', onOutsideClick); // Clean up listener
});

const onOutsideClick = (e) => {
  // Check if click was outside the action picker and trigger
  if (!e.composedPath().includes(triggerEl.value) && !e.composedPath().includes(actionPickerContentEl.value)) {
    actionPickerActive.value = false;
    closeActiveActionPicker(); // Ensure the active action picker is closed
  }
};

// Watchers to respond to prop changes
watch(actionsParsed, () => {
  initPopper();
});
</script>

<style lang="postcss">
@import '@/tailwind.output.css';
.full-width {
  @apply w-full;
}

.trigger {
  @apply cursor-pointer;
  &.disabled {
    @apply cursor-default;
  }
}
.action-picker {
  position: absolute;
  inset: 0px 0px auto auto;
  margin: 0px;
  transform: translate(0px, 30px);
}
.trigger.disabled + .action-picker {
  @apply absolute;
  top: -99999999px !important;
  left: -99999999px !important;
}
.action-picker {
  @apply z-10 inline-block rounded border border-solid border-titan-gray-200 bg-white font-titan-body text-sm shadow;
}

.action-picker-scroller {
  @apply overflow-y-auto py-2;
  scrollbar-color: var(--color-titan-gray-600) white;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    @apply h-full w-[5px];
    &-track {
      @apply rounded bg-white;
    }
    &-thumb {
      @apply rounded bg-titan-gray-600;
    }
  }
}

.dropdown-item {
  @apply flex cursor-pointer items-center justify-start px-3 py-2 text-titan-dark hover:bg-titan-blue-50;
  &.disabled {
    @apply cursor-default !text-titan-gray-600 hover:bg-white;
  }
  &.warning {
    @apply text-titan-yellow;
  }
  &.danger {
    @apply text-titan-red;
  }
  .titan-disabled-message {
    @apply text-xs;
  }
}
</style>
